import React, { useState, useCallback, useMemo } from 'react';
import { TextField, Box, Autocomplete, InputAdornment, CircularProgress, debounce } from '@mui/material';
import useAddressLookup from '../../lib/hooks/useAddressLookup';
import { postValidateAddress } from '../../lib/api';
import { useMediaQuery } from '@mui/material';

const sanitizeInput = (input) => {
  // Basic XSS prevention
  // console.log('input is', input)
  return input?.replace(/[<>]/g, '');
};

const AddressInput = ({ showDeliveryInstructions = false, mode = 'usps', setNewAddress, setNeedUnitNumber }) => {
  const [value, setValue] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [isValidAddressSelected, setIsValidAddressSelected] = useState(false);
  const [isAddressValidating, setIsAddressValidating] = useState(false);
  const [addressValidationFailed, setAddressValidationFailed] = useState(false);

  const { data: suggestions, refetch } = useAddressLookup(value, mode);

  // Debounced address lookup
  const debouncedRefetch = useMemo(() => debounce(() => refetch(), 300), [refetch]);

  const validateAddress = useCallback(
    async (selectedAddress) => {
      if (!selectedAddress) {
        return;
      }

      setIsAddressValidating(true);
      setAddressValidationFailed(false);
      try {
        const fullAddress =
          selectedAddress.street_line +
          ' ' +
          selectedAddress.city +
          ' ' +
          selectedAddress.state +
          ' ' +
          selectedAddress.zipcode;
        const sanitizedAddress = sanitizeInput(fullAddress);
        const validationResponse = await postValidateAddress({ address: sanitizedAddress }, mode);
        // console.log('validationResponse is', validationResponse)
        // Address is fully validated.
        // Address validation incomplete.
        if (validationResponse.message === 'Address validation incomplete.') {
          setNeedUnitNumber(true);
        }

        // Combine sanitized address with selectedAddress
        const combinedAddress = {
          ...selectedAddress,
          sanitizedAddress: sanitizedAddress,
        };

        setNewAddress(combinedAddress);

        setIsValidAddressSelected(validationResponse.isValid);
        setAddressValidationFailed(!validationResponse.isValid);
      } catch (error) {
        console.error('Address validation failed:', error);
        setAddressValidationFailed(true);
      } finally {
        setIsAddressValidating(false);
      }
    },
    [mode],
  );

  const handleAddressChange = useCallback(
    async (event, newValue) => {
      const sanitizedValue = sanitizeInput(newValue);
      // console.log('here is the value', sanitizedValue)
      // console.log('value is string?', typeof sanitizedValue === 'string')
      setValue(sanitizedValue);
      if (sanitizedValue.length > 5) {
        debouncedRefetch();
      }
    },
    [debouncedRefetch],
  );

  const handleSuggestionSelect = useCallback(
    (event, newValue) => {
      if (newValue) {
        const addressValue = newValue.description || newValue;
        setValue(addressValue);
        validateAddress(addressValue);
      }
    },
    [validateAddress],
  );

  const isMobileView = useMediaQuery('(max-width: 600px)');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Autocomplete
        fullWidth
        options={suggestions ? suggestions : []}
        getOptionLabel={(option) => {
          const description = option.description || option.street_line || '';
          const city = option.city ? `, ${option.city}` : '';
          const state = option.state ? `, ${option.state}` : '';
          const zipcode = option.zipcode ? ` ${option.zipcode}` : '';
          return `${description}${city}${state}${zipcode}`;
        }}
        onInputChange={handleAddressChange}
        onChange={handleSuggestionSelect}
        inputValue={typeof value === 'string' ? value : '124'}
        renderInput={(params) => (
          <TextField
            error={addressValidationFailed}
            {...params}
            placeholder="Enter your address"
            fullWidth
            InputProps={{
              ...params.InputProps,
              sx: {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
                color: 'var(--color11, #58489A)',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                height: 'var(--4, 44px)',
                padding: 'var(--3-5, 16px)',
                borderRadius: 'var(--4, 9px)',
                border: '1px solid var(--color8, #A69ECA)',
                background: 'var(--color1, #FDFDFD)',
                width: '100%',
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                },
              },
              startAdornment: null,
            }}
          />
        )}
      />

      {isValidAddressSelected && (
        <TextField
          label="Unit Number"
          value={unitNumber}
          onChange={(e) => setUnitNumber(e.target.value)}
          fullWidth
        />
      )}

      {isValidAddressSelected && showDeliveryInstructions && (
        <TextField
          label="Delivery Instructions"
          value={deliveryInstructions}
          onChange={(e) => setDeliveryInstructions(e.target.value)}
          fullWidth
        />
      )}
    </Box>
  );
};

export default React.memo(AddressInput);
